import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { Link } from '../../components/Link';
import { ArrowRight, Compass, Users, Target, Scale } from 'lucide-react';

const AboutPage: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();
    const about = t?.about;

    if (!about) return null;

    const sections = [
        {
            title: about.overview.title,
            description: about.overview.subtitle,
            icon: Compass,
            link: '/about/overview',
            color: 'bg-blue-50 text-blue-600',
            image: '/assets/imagens/optimized/pexels-kelly-6595788.jpg'
        },
        {
            title: about.board.title,
            description: about.board.subtitle,
            icon: Users,
            link: '/about/board',
            color: 'bg-emerald-50 text-emerald-600',
            image: '/assets/imagens/1581096148238.jpeg'
        },
        {
            title: about.strategy.hero.title,
            description: about.strategy.hero.subtitle,
            icon: Target,
            link: '/about/strategy',
            color: 'bg-purple-50 text-purple-600',
            image: '/assets/imagens/optimized/Gemini_Generated_Image_mit0k8mit0k8mit0.png'
        },
        {
            title: about.governance.title,
            description: about.governance.subtitle,
            icon: Scale,
            link: '/about/governance',
            color: 'bg-amber-50 text-amber-600',
            image: '/assets/imagens/pexels-pixabay-46801.jpg' // Generic fallback
        }
    ];

    useSEO({
        title: "Sobre a NexuCapital",
        description: about.overview.subtitle,
        path: "/about",
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Sobre', url: '/about' }
            ]),
            generateWebPageSchema({
                name: "Sobre a NexuCapital",
                description: about.overview.subtitle,
                url: "/about",
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>
            <main className="flex-grow bg-gray-50">
                {/* Hero */}
                <div className="bg-primary text-white py-24 relative overflow-hidden" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwMDAiLz4KPC9zdmc+')" }}>
                    <div className="container mx-auto px-4 text-center relative z-10">
                        <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6 animate-fade-in-up">
                            Inovação na Mineração
                        </h1>
                        <p className="text-xl max-w-3xl mx-auto text-gray-200 leading-relaxed animate-slide-up-fade">
                            {about.overview.subtitle}
                        </p>
                    </div>
                </div>

                {/* Navigation Grid */}
                <section className="py-20 -mt-10 relative z-20">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                            {sections.map((section, index) => {
                                const Icon = section.icon;
                                return (
                                    <Link
                                        key={index}
                                        to={section.link}
                                        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col h-full border border-gray-100"
                                    >
                                        <div className="h-48 overflow-hidden relative">
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
                                            <img
                                                src={section.image}
                                                alt={section.title}
                                                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                                                onError={(e) => {
                                                    e.currentTarget.src = 'https://placehold.co/600x400/1a1a1a/ffffff?text=NexuCapital';
                                                }}
                                            />
                                            <div className="absolute bottom-4 left-6 z-20 flex items-center gap-3">
                                                <div className={`p-2 rounded-lg bg-white/90 backdrop-blur-sm ${section.color.replace('bg-', 'text-').split(' ')[1]}`}>
                                                    <Icon size={24} />
                                                </div>
                                                <h2 className="text-2xl font-bold text-white shadow-sm">
                                                    {section.title}
                                                </h2>
                                            </div>
                                        </div>

                                        <div className="p-8 flex-grow flex flex-col justify-between">
                                            <p className="text-gray-600 mb-6 leading-relaxed">
                                                {section.description}
                                            </p>

                                            <div className="flex items-center text-primary font-bold group-hover:translate-x-2 transition-transform duration-300">
                                                Saiba mais <ArrowRight className="ml-2 w-4 h-4" />
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            </main>
        </PublicLayout>
    );
};

export default AboutPage;
